import type { VarlessCopyId } from '@npc/copy'

import type {
  DevelopmentTask,
  DevelopmentTaskPreviewReceipt,
  DevelopmentTaskReleaseReceipt,
} from '../../app/development-tasks'
import type { TagTone } from '../../components/tag'

/**
 * 统一用户状态。只许这六档。
 *
 * 已上线只认 production_verified，并且必须带着那一版的 SHA。
 * HTTP 200、本地提交、任务 ready、recorded、executing、gated_push_completed
 * 都不是已上线。
 */
export const USER_DELIVERY_STATUSES = [
  'in_development',
  'fast_test',
  'preview_available',
  'pending_release',
  'live',
  'needs_fix',
] as const

export type UserDeliveryStatus = (typeof USER_DELIVERY_STATUSES)[number]

export type DeliveryActionAvailability = 'available' | 'unavailable'

export type DeliveryRequestState = 'running' | 'success' | 'failure'

export type DeliveryFacts = {
  taskState: string
  ownerSha?: string
  previewState?: string
  previewSha?: string
  previewUrl?: string
  releaseState?: string
  releaseSha?: string
  integrationSha?: string
  productionSha?: string
  issues?: string[]
}

export type DeliveryMission = {
  status: UserDeliveryStatus
  ownerSha: string | null
  integrationSha: string | null
  productionSha: string | null
  previewUrl: string | null
  requestState: DeliveryRequestState | null
  nextStep: VarlessCopyId
  issues: string[]
  actions: {
    openPreview: DeliveryActionAvailability
    fix: DeliveryActionAvailability
  }
}

const SHA = /^[0-9a-f]{40}([0-9a-f]{24})?$/

const RELEASE_RUNNING = new Set(['recorded', 'executing', 'release_requested'])
const RELEASE_FAILURE = new Set([
  'refused',
  'failed',
  'revocation_pending',
  'blocked_later_steps',
  'gated_push_completed',
])

export function validDeliverySha(value: string | undefined): value is string {
  return typeof value === 'string' && SHA.test(value)
}

export function validPreviewUrl(value: string | undefined): value is string {
  if (typeof value !== 'string' || value.length === 0) return false
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

export function mapReleaseRequestState(state: string | undefined): DeliveryRequestState | null {
  if (!state) return null
  if (state === 'production_verified') return 'success'
  if (RELEASE_RUNNING.has(state)) return 'running'
  if (RELEASE_FAILURE.has(state)) return 'failure'
  return 'failure'
}

/**
 * 把任务内部状态、预览回执和正式发布回执收成用户能看的那一档。
 *
 * 优先级从硬到软：已上线只走 production_verified；发布失败压过待发布；
 * recorded/executing 是待发布，不是已上线。没有回执时只看任务状态。
 */
export function mapUserDeliveryStatus(facts: DeliveryFacts): UserDeliveryStatus {
  const releaseLiveSha = facts.releaseState === 'production_verified' ? facts.releaseSha : undefined
  const previewLiveSha = facts.previewState === 'production_verified' ? facts.previewSha : undefined
  const productionSha = facts.productionSha ?? releaseLiveSha ?? previewLiveSha
  if (
    (facts.releaseState === 'production_verified' || facts.previewState === 'production_verified') &&
    validDeliverySha(productionSha)
  ) {
    return 'live'
  }
  if (
    facts.taskState === 'failed' ||
    facts.taskState === 'fix_requested' ||
    facts.taskState === 'cancelled' ||
    facts.previewState === 'preview_failed_rolled_back' ||
    (facts.releaseState !== undefined && RELEASE_FAILURE.has(facts.releaseState))
  ) {
    return 'needs_fix'
  }
  if (
    facts.taskState === 'v2_handoff' ||
    facts.taskState === 'gating' ||
    facts.taskState === 'ci_running' ||
    facts.previewState === 'release_requested' ||
    (facts.releaseState !== undefined && RELEASE_RUNNING.has(facts.releaseState))
  ) {
    return 'pending_release'
  }
  if (facts.previewState === 'test_candidate') return 'preview_available'
  if (facts.previewState === 'preview_ready' || facts.previewState === 'preview_running') return 'fast_test'
  return 'in_development'
}

export function missionFromFacts(facts: DeliveryFacts): DeliveryMission {
  const status = mapUserDeliveryStatus(facts)
  const ownerSha = validDeliverySha(facts.ownerSha) ? facts.ownerSha : null
  const integrationSha = validDeliverySha(facts.integrationSha) ? facts.integrationSha : null
  const productionSha =
    status === 'live' && validDeliverySha(facts.productionSha ?? facts.releaseSha ?? facts.previewSha)
      ? (facts.productionSha ?? facts.releaseSha ?? facts.previewSha ?? null)
      : null
  const previewUrl = validPreviewUrl(facts.previewUrl) ? facts.previewUrl : null
  const requestState = mapReleaseRequestState(facts.releaseState)
  return {
    status,
    ownerSha,
    integrationSha,
    productionSha,
    previewUrl,
    requestState,
    nextStep: nextStepCopy(status, facts, previewUrl),
    issues: facts.issues ?? [],
    actions: {
      openPreview: previewUrl ? 'available' : 'unavailable',
      fix: status === 'needs_fix' ? 'available' : 'unavailable',
    },
  }
}

function factsIssues(
  release?: Pick<DevelopmentTaskReleaseReceipt, 'state' | 'failure_code'>,
): string[] | undefined {
  if (!release || !RELEASE_FAILURE.has(release.state)) return undefined
  const code = typeof release.failure_code === 'string' ? release.failure_code.trim() : ''
  return code ? [code] : undefined
}

export function factsFromTask(
  task: Pick<DevelopmentTask, 'state' | 'current_attempt'> & {
    production_verified_at?: string | null
    deployment_sha?: string | null
    lineage_relation?: string | null
    candidate_sha?: string | null
  },
  receipt?: Pick<DevelopmentTaskPreviewReceipt, 'id' | 'sha' | 'lane' | 'state' | 'version' | 'preview_url'>,
  release?: Pick<DevelopmentTaskReleaseReceipt, 'state' | 'sha' | 'rollback_sha' | 'failure_code'>,
): DeliveryFacts {
  const rowLive = task.production_verified_at ? task.deployment_sha ?? undefined : undefined
  const releaseLive = release?.state === 'production_verified' ? release.sha : undefined
  const previewLive = receipt?.state === 'production_verified' ? receipt.sha : undefined
  return {
    taskState: task.state,
    ownerSha: task.current_attempt?.result_sha ?? task.candidate_sha ?? undefined,
    previewState: receipt?.state,
    previewSha: receipt?.sha,
    previewUrl: receipt?.preview_url ?? undefined,
    releaseState: task.production_verified_at ? 'production_verified' : release?.state,
    releaseSha: release?.sha,
    productionSha: rowLive ?? releaseLive ?? previewLive,
    issues: factsIssues(release),
  }
}

export const statusCopy: Record<UserDeliveryStatus, VarlessCopyId> = {
  in_development: 'development_task.mission.status.in_development',
  fast_test: 'development_task.mission.status.fast_test',
  preview_available: 'development_task.mission.status.preview_available',
  pending_release: 'development_task.mission.status.pending_release',
  live: 'development_task.mission.status.live',
  needs_fix: 'development_task.mission.status.needs_fix',
}

export const statusTone: Record<UserDeliveryStatus, TagTone> = {
  in_development: 'info',
  fast_test: 'info',
  preview_available: 'ok',
  pending_release: 'warn',
  live: 'ok',
  needs_fix: 'danger',
}

export const requestStateCopy: Record<DeliveryRequestState, VarlessCopyId> = {
  running: 'development_task.mission.request.running',
  success: 'development_task.mission.request.success',
  failure: 'development_task.mission.request.failure',
}

export const requestStateTone: Record<DeliveryRequestState, TagTone> = {
  running: 'warn',
  success: 'ok',
  failure: 'danger',
}

function nextStepCopy(
  status: UserDeliveryStatus,
  facts: DeliveryFacts,
  previewUrl: string | null,
): VarlessCopyId {
  if (facts.taskState === 'cancelled') return 'development_task.mission.next.cancelled'
  if (status === 'preview_available' && !previewUrl) return 'development_task.mission.next.preview_available_no_url'
  switch (status) {
    case 'in_development':
      return 'development_task.mission.next.in_development'
    case 'fast_test':
      return 'development_task.mission.next.fast_test'
    case 'preview_available':
      return 'development_task.mission.next.preview_available'
    case 'pending_release':
      return 'development_task.mission.next.pending_release'
    case 'live':
      return 'development_task.mission.next.live'
    case 'needs_fix':
      return 'development_task.mission.next.needs_fix'
  }
}
